import React, { useState, useEffect } from 'react';
import * as XLSX from 'xlsx';
import { collection, doc, updateDoc, getDocs, query, where, addDoc } from 'firebase/firestore';
import { db } from '../firebase/config.ts';
import { Employee } from '../types/employee';
import { useAuth } from '../contexts/AuthContext.tsx';

interface ImportProgress {
  total: number;
  processed: number;
  matched: number;
  updated: number;
  errors: Array<{ row: number; name: string; error: string }>;
}

interface MatchingRow {
  row: number;
  excelName: string;
  data: ExcelRow;
  suggestedEmployee: Employee | null;
  selectedEmployeeId: string | null;
  isNewEmployee?: boolean;
}

interface ExcelRow {
  // Legacy fields
  'Employee Name:'?: string;
  'Employee Name'?: string;
  'Employee'?: string;
  'Regular rate (hourly)'?: number;
  'FICA'?: number;
  'Insurance'?: number;
  'Vacation/Holiday'?: number;
  'Phone Number'?: string;
  'Employee Start Date'?: string;
  'TDLR Number'?: string;
  'TDLR Expiration'?: string;
  'Hire Date:'?: string;
  'Hire Date'?: string;
  'Job Title:'?: string;
  'Job Title'?: string;
  'Wage:'?: number;
  'Wage'?: number;
  'CEC MARKUP'?: number;
  'Rate'?: number;
  'CEC RATE'?: number;
  'CEC OT'?: number;
  'RELIABLE MARKUP'?: number;
  'RELIABLE RATE'?: number;
  'RELIABLE OT RATE'?: number;
  'BTC MARKUP'?: number;
  'BTC RATE'?: number;
  'BTC OT RATE'?: number;
  'TDLR'?: string;
  'Notes'?: string;
  'TDLR Expires'?: string;
  'BTC MTE'?: string;
  'RELIABLE'?: string;
  'CAR/FUEL'?: number;
  'Hourly'?: number;
  
  // Gusto Weekly Report fields
  'Current department'?: string;
  'Employment status'?: string;
  'Employee start date'?: string;
  'TDLR Expiration'?: string;
  'Phone number'?: string;
  'rate (hourly)'?: number;
  'Regular'?: number;
  'Time'?: number;
  [key: string]: any; // Allow dynamic fields
}

const EmployeeDataImporter: React.FC<{ onClose: () => void; onSuccess: () => void }> = ({ onClose, onSuccess }) => {
  const { currentUser } = useAuth();
  const [file, setFile] = useState<File | null>(null);
  const [importing, setImporting] = useState(false);
  const [progress, setProgress] = useState<ImportProgress | null>(null);
  const [previewData, setPreviewData] = useState<ExcelRow[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [matchingMode, setMatchingMode] = useState(false);
  const [matchingRows, setMatchingRows] = useState<MatchingRow[]>([]);
  const [allEmployees, setAllEmployees] = useState<Employee[]>([]);
  const [showColumnMapping, setShowColumnMapping] = useState(false);
  const [excelHeaders, setExcelHeaders] = useState<string[]>([]);
  const [rawExcelData, setRawExcelData] = useState<any[]>([]);
  const [columnMapping, setColumnMapping] = useState({
    employeeName: '',
    phoneNumber: '',
    hireDate: '',
    jobTitle: '',
    wage: '',
    employmentStatus: '',
    tdlr: ''
  });
  const [columnExamples, setColumnExamples] = useState<any>({});
  const [defaultInsurance, setDefaultInsurance] = useState(1.07);
  const [defaultVacation, setDefaultVacation] = useState(0.97);
  const [showDefaultsPrompt, setShowDefaultsPrompt] = useState(false);

  useEffect(() => {
    fetchAllEmployees();
  }, []);

  const fetchAllEmployees = async () => {
    try {
      const employeesRef = collection(db, 'employees');
      const snapshot = await getDocs(employeesRef);
      const employees = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as Employee));
      setAllEmployees(employees);
    } catch (error) {
      console.error('Error fetching employees:', error);
    }
  };

  // Helper function to get value from Excel row with flexible column names
  const getExcelValue = (row: ExcelRow, field: string): any => {
    return row[field as keyof ExcelRow] || row[`${field}:` as keyof ExcelRow];
  };

  const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = event.target.files?.[0];
    if (!selectedFile) return;

    setFile(selectedFile);
    setError(null);
    
    try {
      const workbook = XLSX.read(await selectedFile.arrayBuffer(), { type: 'array' });
      const worksheet = workbook.Sheets[workbook.SheetNames[0]];
      
      // Get headers from row 5 (skip first 4 rows for Gusto reports)
      const range = XLSX.utils.decode_range(worksheet['!ref'] || 'A1');
      const headers: string[] = [];
      
      // Get headers from row 5 (index 4) - skip Gusto report header rows
      const headerRowIndex = 4;
      const allRows = XLSX.utils.sheet_to_json(worksheet, { header: 1 });
      const firstRowData = allRows[headerRowIndex] as any[];
      
      if (firstRowData && Array.isArray(firstRowData)) {
        for (let i = 0; i < firstRowData.length; i++) {
          const cellValue = firstRowData[i];
          if (cellValue !== undefined && cellValue !== null && String(cellValue).trim() !== '') {
            headers.push(String(cellValue).trim());
          } else {
            // Even if empty, add placeholder to maintain column order
            headers.push(`Column ${i + 1}`);
          }
        }
      } else {
        // Fallback to the original method but use row 5 (index 4)
        for (let col = range.s.c; col <= range.e.c; col++) {
          const cellAddress = XLSX.utils.encode_cell({ r: headerRowIndex, c: col });
          const cell = worksheet[cellAddress];
          if (cell && cell.v) {
            headers.push(String(cell.v).trim());
          } else {
            headers.push(`Column ${col + 1}`);
          }
        }
      }
      
      setExcelHeaders(headers);
      
      // Get all data
      const jsonData = XLSX.utils.sheet_to_json(worksheet, { header: 1 });
      setRawExcelData(jsonData);
      
      // Create examples from first few data rows (starting from row 6, index 5)
      const examples: any = {};
      const dataStartRow = headerRowIndex + 1; // Row 6 (index 5)
      for (let i = 0; i < headers.length; i++) {
        const header = headers[i];
        const exampleValues = [];
        for (let row = dataStartRow; row < Math.min(dataStartRow + 5, jsonData.length); row++) {
          const rowData = jsonData[row] as any[];
          if (rowData && rowData[i] !== undefined && rowData[i] !== null && String(rowData[i]).trim() !== '') {
            exampleValues.push(String(rowData[i]).trim());
          }
        }
        examples[header] = exampleValues.filter(v => v && v !== 'undefined').slice(0, 3);
      }
      setColumnExamples(examples);
      
      // Auto-detect common column mappings - enhanced for Gusto reports
      const detectedMapping = { ...columnMapping };
      headers.forEach(header => {
        const lowerHeader = header.toLowerCase().replace(/[:\s()]/g, '');
        
        // Enhanced detection for Gusto weekly reports
        if (lowerHeader.includes('employee') && !lowerHeader.includes('start') && !lowerHeader.includes('number')) {
          // Prioritize fields that are just "Employee" or "Employee Name"
          if (lowerHeader === 'employee' || lowerHeader.includes('employeename')) {
            detectedMapping.employeeName = header;
          }
        } else if (lowerHeader.includes('currentdepartment') || (lowerHeader.includes('current') && lowerHeader.includes('department'))) {
          detectedMapping.jobTitle = header;
        } else if (lowerHeader === 'employmentstatus' || (lowerHeader.includes('employment') && lowerHeader.includes('status'))) {
          detectedMapping.employmentStatus = header;
        } else if (lowerHeader === 'ratehourly' || lowerHeader.includes('ratehourly') || lowerHeader === 'regularratehourly') {
          detectedMapping.wage = header;
        } else if (lowerHeader === 'regular' && !lowerHeader.includes('rate')) {
          // Sometimes "Regular" column contains the hourly rate
          detectedMapping.wage = header;
        } else if (lowerHeader === 'phonenumber' || lowerHeader.includes('phone')) {
          detectedMapping.phoneNumber = header;
        } else if (lowerHeader === 'employeestartdate' || (lowerHeader.includes('employee') && lowerHeader.includes('start'))) {
          detectedMapping.hireDate = header;
        } else if (lowerHeader === 'tdlrnumber' || (lowerHeader.includes('tdlr') && lowerHeader.includes('number'))) {
          detectedMapping.tdlr = header;
        }
      });
      
      setColumnMapping(detectedMapping);
      setShowColumnMapping(true);
      
    } catch (error) {
      console.error('Error processing file:', error);
      setError('Error reading Excel file. Please check the file format.');
    }
  };

  const findBestMatch = (name: string): Employee | null => {
    if (!name?.trim() || allEmployees.length === 0) return null;
    
    const normalizedName = name.trim().toLowerCase();
    
    // Try exact match first
    let bestMatch = allEmployees.find(emp => 
      emp.name?.toLowerCase() === normalizedName
    );
    if (bestMatch) return bestMatch;

    // Try firstName + lastName match
    const nameParts = normalizedName.split(/\s+/);
    if (nameParts.length >= 2) {
      const firstName = nameParts[0];
      const lastName = nameParts[nameParts.length - 1];
      
      bestMatch = allEmployees.find(employee => {
        const empFirstName = employee.firstName?.toLowerCase() || '';
        const empLastName = employee.lastName?.toLowerCase() || '';
        const empFullName = employee.name?.toLowerCase() || '';
        
        return (
          (empFirstName === firstName && empLastName === lastName) ||
          (empFullName.includes(firstName) && empFullName.includes(lastName)) ||
          (normalizedName.includes(empFirstName) && normalizedName.includes(empLastName) && empFirstName && empLastName)
        );
      });
      
      if (bestMatch) return bestMatch;
    }
    
    // Fuzzy matching - find names that contain parts of the search
    bestMatch = allEmployees.find(employee => {
      const empFullName = employee.name?.toLowerCase() || '';
      const empFirstName = employee.firstName?.toLowerCase() || '';
      const empLastName = employee.lastName?.toLowerCase() || '';
      
      return nameParts.some(part => 
        part.length > 2 && (
          empFullName.includes(part) || 
          empFirstName.includes(part) || 
          empLastName.includes(part)
        )
      );
    });
    
    return bestMatch || null;
  };

  const findEmployeeById = (id: string): Employee | null => {
    return allEmployees.find(emp => emp.id === id) || null;
  };

  const parseDate = (dateStr: string): Date | null => {
    if (!dateStr) return null;
    
    // Handle Excel date serial numbers
    if (typeof dateStr === 'number') {
      return new Date((dateStr - 25569) * 86400 * 1000);
    }
    
    // Handle string dates
    const date = new Date(dateStr);
    return isNaN(date.getTime()) ? null : date;
  };

  const calculateBurdenedRate = (wage: number, fica: number, insurance: number, vacation: number, carFuel: number): number => {
    return wage + (fica || 0) + (insurance || 0) + (vacation || 0) + (carFuel || 0);
  };

  const processImport = async () => {
    if (!matchingRows.length) return;

    // Check if there are new employees and prompt for defaults
    const hasNewEmployees = matchingRows.some(row => row.selectedEmployeeId === 'CREATE_NEW');
    if (hasNewEmployees && !showDefaultsPrompt) {
      setShowDefaultsPrompt(true);
      return;
    }

    setImporting(true);
    setMatchingMode(false);
    setShowDefaultsPrompt(false);

    const progress: ImportProgress = {
      total: matchingRows.length,
      processed: 0,
      matched: 0,
      updated: 0,
      errors: []
    };

    setProgress(progress);

    for (const matchingRow of matchingRows) {
      try {
        progress.processed++;
        
        const employee = matchingRow.selectedEmployeeId 
          ? findEmployeeById(matchingRow.selectedEmployeeId)
          : null;
        
        const row = matchingRow.data;
        
        if (!employee && matchingRow.selectedEmployeeId !== 'CREATE_NEW') {
          // Skip rows where no employee is selected and not marked for creation
          continue;
        }

        progress.matched++;

        if (matchingRow.selectedEmployeeId === 'CREATE_NEW' || !employee) {
          // Create new employee
          const newEmployeeData: Partial<Employee> = {
            name: matchingRow.excelName,
            createdAt: new Date(),
            updatedAt: new Date(),
            createdBy: currentUser?.uid,
            status: 'active'
          };

          // Basic info
          if (row.phoneNumber) newEmployeeData.phone = String(row.phoneNumber);
          if (row.hireDate) newEmployeeData.hireDate = parseDate(row.hireDate) || undefined;
          if (row.jobTitle) newEmployeeData.jobTitle = String(row.jobTitle);
          if (row.wage) newEmployeeData.hourlyWage = Number(row.wage);
          
          // Handle employment status for new employees
          if (row.employmentStatus) {
            const empStatus = String(row.employmentStatus).toLowerCase();
            if (empStatus.includes('terminated') || empStatus.includes('inactive')) {
              newEmployeeData.status = 'terminated';
              newEmployeeData.terminationDate = new Date();
            } else {
              newEmployeeData.status = 'active';
            }
          }

          // Use defaults for new employees
          const wage = newEmployeeData.hourlyWage || 0;
          const fica = wage * 0.0765; // Auto-calculate FICA at 7.65%
          const insurance = defaultInsurance;
          const vacation = defaultVacation;
          const carFuel = 0; // Default to 0
          
          newEmployeeData.fica = fica;
          newEmployeeData.insurance = insurance;
          newEmployeeData.vacation = vacation;
          newEmployeeData.carFuel = carFuel;
          newEmployeeData.burdenedRate = calculateBurdenedRate(wage, fica, insurance, vacation, carFuel);

          // TDLR info - only for new employees
          if (row.tdlr) newEmployeeData.tdlrNumber = String(row.tdlr);

          // Add the new employee
          await addDoc(collection(db, 'employees'), newEmployeeData);
          progress.updated++;
          
        } else {
          // Update existing employee
          const updateData: Partial<Employee> = {
            updatedAt: new Date(),
            lastGustoSync: new Date() // Track when this was last synced from Gusto
          };

          // Handle employment status changes - map to our status field
          if (row.employmentStatus) {
            const empStatus = String(row.employmentStatus).toLowerCase();
            if (empStatus.includes('terminated') || empStatus.includes('inactive')) {
              updateData.status = 'terminated';
              updateData.terminationDate = new Date();
            } else if (empStatus.includes('active')) {
              updateData.status = 'active';
              // Clear termination date if they were terminated but are now active
              if (employee.status === 'terminated') {
                updateData.terminationDate = null;
              }
            }
          }

          // Basic info - only update if values are different
          if (row.phoneNumber && String(row.phoneNumber) !== employee.phone) {
            updateData.phone = String(row.phoneNumber);
          }
          if (row.hireDate) {
            const newHireDate = parseDate(row.hireDate);
            if (newHireDate && newHireDate.getTime() !== employee.hireDate?.getTime()) {
              updateData.hireDate = newHireDate;
            }
          }
          if (row.jobTitle && String(row.jobTitle) !== employee.jobTitle) {
            updateData.jobTitle = String(row.jobTitle);
          }
          if (row.wage && Number(row.wage) !== employee.hourlyWage) {
            updateData.hourlyWage = Number(row.wage);
            
            // Recalculate FICA based on new wage
            const newWage = Number(row.wage);
            const newFica = newWage * 0.0765; // Auto-calculate FICA at 7.65%
            updateData.fica = newFica;
            
            // Recalculate burdened rate with updated wage and FICA
            const currentInsurance = employee.insurance ?? 0;
            const currentVacation = employee.vacation ?? 0;
            const currentCarFuel = employee.carFuel ?? 0;
            
            updateData.burdenedRate = calculateBurdenedRate(newWage, newFica, currentInsurance, currentVacation, currentCarFuel);
          }

          // TDLR number - only update if provided and different (but NOT expiration for existing employees)
          if (row.tdlr && String(row.tdlr) !== employee.tdlrNumber) {
            updateData.tdlrNumber = String(row.tdlr);
          }

          // Only update if there are actual changes (beyond just updatedAt)
          const hasChanges = Object.keys(updateData).length > 2; // More than updatedAt and lastGustoSync
          
          if (hasChanges) {
            await updateDoc(doc(db, 'employees', employee.id), updateData);
            progress.updated++;
          }
        }

      } catch (error) {
        progress.errors.push({
          row: matchingRow.row,
          name: matchingRow.excelName || 'Unknown',
          error: error instanceof Error ? error.message : 'Unknown error'
        });
      }

      setProgress({ ...progress });
    }

    setImporting(false);
    
    if (progress.updated > 0) {
      onSuccess();
    }
  };

  const updateEmployeeMatch = (rowIndex: number, employeeId: string | null) => {
    setMatchingRows(prev => 
      prev.map((row, index) => 
        index === rowIndex 
          ? { ...row, selectedEmployeeId: employeeId }
          : row
      )
    );
  };

  const getMatchedCount = () => {
    return matchingRows.filter(row => row.selectedEmployeeId && row.selectedEmployeeId !== '').length;
  };

  const processColumnMapping = async () => {
    if (!columnMapping.employeeName) {
      setError('Please select the Employee Name column');
      return;
    }

    setError(null);
    
    try {
      // Convert raw data using column mapping - use headers from row 5 and start data from row 6
      const headers = rawExcelData[4] as string[]; // Headers from row 5 (index 4)
      const processedData: MatchingRow[] = [];

      for (let i = 5; i < rawExcelData.length; i++) { // Start from row 6 (index 5)
        const row = rawExcelData[i] as any[];
        if (!row || row.length === 0) continue;

        // Create a mapped row object
        const mappedRow: any = {};
        Object.entries(columnMapping).forEach(([field, excelColumn]) => {
          if (excelColumn) {
            const columnIndex = headers.indexOf(excelColumn);
            if (columnIndex !== -1 && row[columnIndex] !== undefined) {
              mappedRow[field] = row[columnIndex];
            }
          }
        });

        const employeeName = mappedRow.employeeName;
        if (employeeName && String(employeeName).trim()) {
          const suggestedEmployee = findBestMatch(String(employeeName).trim());
          processedData.push({
            row: i + 1,
            excelName: String(employeeName).trim(),
            data: mappedRow,
            suggestedEmployee,
            selectedEmployeeId: suggestedEmployee?.id || null,
            isNewEmployee: !suggestedEmployee
          });
        }
      }

      setMatchingRows(processedData);
      setShowColumnMapping(false);
      
      // Create preview data for display
      const preview = processedData.slice(0, 5).map(item => ({
        'Employee Name': item.excelName,
        'Job Title': item.data.jobTitle || '',
        'Wage': item.data.wage || '',
        'CEC Rate': item.data.cecRate || '',
        'TDLR': item.data.tdlr || ''
      }));
      setPreviewData(preview as any);
      
    } catch (error) {
      console.error('Error processing column mapping:', error);
      setError('Error processing column mapping. Please check your selections.');
    }
  };

  const getUpdateSummary = (row: MatchingRow): string => {
    const updates = [];
    if (row.data.phoneNumber) updates.push('Phone');
    if (row.data.hireDate) updates.push('Hire Date');
    if (row.data.jobTitle) updates.push('Job Title');
    if (row.data.wage) updates.push('Wage (+ FICA)');
    if (row.data.employmentStatus) updates.push('Employment Status');
    if (row.data.tdlr) updates.push('TDLR Number');
    
    return updates.length > 0 ? updates.join(', ') : 'No updates';
  };

  const proceedToMatching = () => {
    setMatchingMode(true);
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 w-full max-w-6xl max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-bold">Import Employee Data</h2>
          <button onClick={onClose} className="text-gray-500 hover:text-gray-700">
            ✕
          </button>
        </div>

        {!file && (
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Select Excel File
            </label>
            <input
              type="file"
              accept=".xlsx,.xls"
              onChange={handleFileUpload}
              className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
            />
          </div>
        )}

        {showColumnMapping && (
          <div className="mb-4">
            <h3 className="text-lg font-semibold mb-4">Map Excel Columns to Employee Fields</h3>
            <div className="text-sm text-gray-600 mb-4">
              Please map your Excel columns to the corresponding employee fields. Required fields are marked with *.
            </div>
            
            <div className="max-h-96 overflow-y-auto">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                
                {/* Employee Name - Required */}
                <div className="border border-red-200 rounded p-3 bg-red-50">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Employee Name *
                  </label>
                  <select
                    value={columnMapping.employeeName}
                    onChange={(e) => setColumnMapping({...columnMapping, employeeName: e.target.value})}
                    className="w-full text-sm border border-gray-300 rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="">-- Select Column --</option>
                    {excelHeaders.map(header => (
                      <option key={header} value={header}>{header}</option>
                    ))}
                  </select>
                  {columnMapping.employeeName && columnExamples[columnMapping.employeeName] && (
                    <div className="text-xs text-gray-500 mt-1">
                      Examples: {columnExamples[columnMapping.employeeName].join(', ')}
                    </div>
                  )}
                </div>

                {/* Phone Number Field */}
                <div className="border rounded p-3">
                  <label className="block text-sm font-medium text-gray-700 mb-2">Phone Number</label>
                  <select
                    value={columnMapping.phoneNumber}
                    onChange={(e) => setColumnMapping({...columnMapping, phoneNumber: e.target.value})}
                    className="w-full text-sm border border-gray-300 rounded px-3 py-2"
                  >
                    <option value="">-- Select Column --</option>
                    {excelHeaders.map(header => (
                      <option key={header} value={header}>{header}</option>
                    ))}
                  </select>
                  {columnMapping.phoneNumber && columnExamples[columnMapping.phoneNumber] && (
                    <div className="text-xs text-gray-500 mt-1">
                      Examples: {columnExamples[columnMapping.phoneNumber].join(', ')}
                    </div>
                  )}
                </div>

                {/* Basic Info Fields */}
                <div className="border rounded p-3">
                  <label className="block text-sm font-medium text-gray-700 mb-2">Hire Date</label>
                  <select
                    value={columnMapping.hireDate}
                    onChange={(e) => setColumnMapping({...columnMapping, hireDate: e.target.value})}
                    className="w-full text-sm border border-gray-300 rounded px-3 py-2"
                  >
                    <option value="">-- Select Column --</option>
                    {excelHeaders.map(header => (
                      <option key={header} value={header}>{header}</option>
                    ))}
                  </select>
                  {columnMapping.hireDate && columnExamples[columnMapping.hireDate] && (
                    <div className="text-xs text-gray-500 mt-1">
                      Examples: {columnExamples[columnMapping.hireDate].join(', ')}
                    </div>
                  )}
                </div>

                <div className="border rounded p-3">
                  <label className="block text-sm font-medium text-gray-700 mb-2">Job Title / Department</label>
                  <select
                    value={columnMapping.jobTitle}
                    onChange={(e) => setColumnMapping({...columnMapping, jobTitle: e.target.value})}
                    className="w-full text-sm border border-gray-300 rounded px-3 py-2"
                  >
                    <option value="">-- Select Column --</option>
                    {excelHeaders.map(header => (
                      <option key={header} value={header}>{header}</option>
                    ))}
                  </select>
                  {columnMapping.jobTitle && columnExamples[columnMapping.jobTitle] && (
                    <div className="text-xs text-gray-500 mt-1">
                      Examples: {columnExamples[columnMapping.jobTitle].join(', ')}
                    </div>
                  )}
                </div>

                <div className="border rounded p-3">
                  <label className="block text-sm font-medium text-gray-700 mb-2">Employment Status</label>
                  <select
                    value={columnMapping.employmentStatus}
                    onChange={(e) => setColumnMapping({...columnMapping, employmentStatus: e.target.value})}
                    className="w-full text-sm border border-gray-300 rounded px-3 py-2"
                  >
                    <option value="">-- Select Column --</option>
                    {excelHeaders.map(header => (
                      <option key={header} value={header}>{header}</option>
                    ))}
                  </select>
                  {columnMapping.employmentStatus && columnExamples[columnMapping.employmentStatus] && (
                    <div className="text-xs text-gray-500 mt-1">
                      Examples: {columnExamples[columnMapping.employmentStatus].join(', ')}
                    </div>
                  )}
                  <div className="text-xs text-gray-600 mt-1">
                    Maps Active → active, Terminated → terminated
                  </div>
                </div>

                <div className="border rounded p-3">
                  <label className="block text-sm font-medium text-gray-700 mb-2">Hourly Wage</label>
                  <select
                    value={columnMapping.wage}
                    onChange={(e) => setColumnMapping({...columnMapping, wage: e.target.value})}
                    className="w-full text-sm border border-gray-300 rounded px-3 py-2"
                  >
                    <option value="">-- Select Column --</option>
                    {excelHeaders.map(header => (
                      <option key={header} value={header}>{header}</option>
                    ))}
                  </select>
                  {columnMapping.wage && columnExamples[columnMapping.wage] && (
                    <div className="text-xs text-gray-500 mt-1">
                      Examples: {columnExamples[columnMapping.wage].join(', ')}
                    </div>
                  )}
                </div>

                {/* TDLR Field */}
                <div className="border rounded p-3">
                  <label className="block text-sm font-medium text-gray-700 mb-2">TDLR Number</label>
                  <select
                    value={columnMapping.tdlr}
                    onChange={(e) => setColumnMapping({...columnMapping, tdlr: e.target.value})}
                    className="w-full text-sm border border-gray-300 rounded px-3 py-2"
                  >
                    <option value="">-- Select Column --</option>
                    {excelHeaders.map(header => (
                      <option key={header} value={header}>{header}</option>
                    ))}
                  </select>
                  {columnMapping.tdlr && columnExamples[columnMapping.tdlr] && (
                    <div className="text-xs text-gray-500 mt-1">
                      Examples: {columnExamples[columnMapping.tdlr].join(', ')}
                    </div>
                  )}
                  <div className="text-xs text-gray-600 mt-1">
                    Note: TDLR expiration dates are only set for new employees
                  </div>
                </div>

              </div>
            </div>

            {error && (
              <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded text-red-700 text-sm">
                {error}
              </div>
            )}
            
            <div className="mt-6 flex justify-between items-center">
              <div className="text-sm text-gray-600">
                Found {excelHeaders.length} columns in your Excel file
              </div>
              <div className="space-x-2">
                <button
                  onClick={() => {
                    setFile(null);
                    setShowColumnMapping(false);
                    setExcelHeaders([]);
                    setColumnMapping({
                      employeeName: '', phoneNumber: '', hireDate: '', jobTitle: '', wage: '', employmentStatus: '', tdlr: ''
                    });
                  }}
                  className="px-4 py-2 text-gray-600 border border-gray-300 rounded hover:bg-gray-50"
                >
                  Choose Different File
                </button>
                <button
                  onClick={processColumnMapping}
                  disabled={!columnMapping.employeeName}
                  className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Process Mapping
                </button>
              </div>
            </div>
          </div>
        )}

        {previewData && !matchingMode && !importing && !progress && (
          <div className="mb-4">
            <h3 className="text-lg font-semibold mb-2">Preview (First 5 rows)</h3>
            <div className="overflow-x-auto">
              <table className="min-w-full border border-gray-300">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="border border-gray-300 px-2 py-1 text-xs">Employee Name</th>
                    <th className="border border-gray-300 px-2 py-1 text-xs">Job Title</th>
                    <th className="border border-gray-300 px-2 py-1 text-xs">Wage</th>
                    <th className="border border-gray-300 px-2 py-1 text-xs">CEC Rate</th>
                    <th className="border border-gray-300 px-2 py-1 text-xs">TDLR</th>
                  </tr>
                </thead>
                <tbody>
                  {previewData.map((row, index) => (
                    <tr key={index}>
                      <td className="border border-gray-300 px-2 py-1 text-xs">{getExcelValue(row, 'Employee Name')}</td>
                      <td className="border border-gray-300 px-2 py-1 text-xs">{getExcelValue(row, 'Job Title')}</td>
                      <td className="border border-gray-300 px-2 py-1 text-xs">{getExcelValue(row, 'Wage') || getExcelValue(row, 'Hourly')}</td>
                      <td className="border border-gray-300 px-2 py-1 text-xs">{getExcelValue(row, 'CEC RATE')}</td>
                      <td className="border border-gray-300 px-2 py-1 text-xs">{getExcelValue(row, 'TDLR')}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="mt-4 flex justify-between items-center">
              <div className="text-sm text-gray-600">
                Found {matchingRows.length} employees to process. 
                {getMatchedCount()} automatically matched.
              </div>
              <div className="space-x-2">
                <button
                  onClick={() => {
                    setFile(null);
                    setPreviewData(null);
                    setMatchingRows([]);
                    setMatchingMode(false);
                  }}
                  className="px-4 py-2 text-gray-600 border border-gray-300 rounded hover:bg-gray-50"
                >
                  Choose Different File
                </button>
                <button
                  onClick={proceedToMatching}
                  className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
                >
                  Review Matches ({getMatchedCount()}/{matchingRows.length})
                </button>
              </div>
            </div>
          </div>
        )}

        {matchingMode && !importing && !progress && (
          <div className="mb-4">
            <h3 className="text-lg font-semibold mb-4">Review Employee Matches</h3>
            <div className="text-sm text-gray-600 mb-4">
              Please review and adjust the employee matches below. You can change any automatic matches or select employees for unmatched rows.
            </div>
            
            <div className="max-h-96 overflow-y-auto border border-gray-200 rounded">
              <table className="min-w-full">
                <thead className="bg-gray-50 sticky top-0">
                  <tr>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Excel Name</th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Job Title</th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Match To Employee</th>
                    <th className="px-3 py-2 text-center text-xs font-medium text-gray-500 uppercase">Status</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {matchingRows.map((row, index) => (
                    <tr key={index} className="hover:bg-gray-50">
                      <td className="px-3 py-2 text-sm font-medium text-gray-900">
                        {row.excelName}
                        <div className="text-xs text-gray-500">Row {row.row}</div>
                        <div className="text-xs text-blue-600 mt-1">
                          Will update: {getUpdateSummary(row)}
                        </div>
                      </td>
                      <td className="px-3 py-2 text-sm text-gray-600">
                        {row.data.jobTitle || '-'}
                        <div className="text-xs text-gray-500">
                          Wage: {row.data.wage ? `$${row.data.wage}` : '-'}
                        </div>
                      </td>
                      <td className="px-3 py-2">
                        <select
                          value={row.selectedEmployeeId || ''}
                          onChange={(e) => updateEmployeeMatch(index, e.target.value || null)}
                          className="w-full text-sm border border-gray-300 rounded px-2 py-1 focus:outline-none focus:ring-2 focus:ring-blue-500"
                        >
                          <option value="">-- Select Option --</option>
                          <option value="CREATE_NEW" className="font-semibold text-green-700">✨ Create New Employee</option>
                          <optgroup label="Existing Employees">
                            {allEmployees
                              .sort((a, b) => (a.name || '').localeCompare(b.name || ''))
                              .map(emp => (
                              <option key={emp.id} value={emp.id}>
                                {emp.name || `${emp.firstName} ${emp.lastName}`} 
                                {emp.jobTitle && ` - ${emp.jobTitle}`}
                                {emp.hourlyWage && ` ($${emp.hourlyWage}/hr)`}
                              </option>
                            ))}
                          </optgroup>
                        </select>
                        {row.suggestedEmployee && (
                          <div className="text-xs text-blue-600 mt-1">
                            Suggested: {row.suggestedEmployee.name}
                          </div>
                        )}
                        {row.selectedEmployeeId && row.selectedEmployeeId !== 'CREATE_NEW' && (
                          <div className="text-xs text-gray-500 mt-2 p-2 bg-gray-50 rounded">
                            <strong>Current Employee Values:</strong>
                            {(() => {
                              const emp = allEmployees.find(e => e.id === row.selectedEmployeeId);
                              if (!emp) return null;
                              return (
                                <div>
                                  <div>Wage: ${emp.hourlyWage || 0}/hr → ${row.data.wage || 'no change'}/hr</div>
                                  <div>Job: {emp.jobTitle || 'none'} → {row.data.jobTitle || 'no change'}</div>
                                  {row.data.tdlr && <div>TDLR: {emp.tdlrNumber || 'none'} → {row.data.tdlr}</div>}
                                  {row.data.cecRate && <div>CEC Rate: ${emp.rates?.cec || emp.cecRate || 0} → ${row.data.cecRate}</div>}
                                </div>
                              );
                            })()}
                          </div>
                        )}
                        {row.selectedEmployeeId === 'CREATE_NEW' && (
                          <div className="text-xs text-green-600 mt-2 p-2 bg-green-50 rounded">
                            <strong>✨ Will create new employee:</strong>
                            <div>Name: {row.excelName}</div>
                            {row.data.wage && <div>Wage: ${row.data.wage}/hr (FICA: ${(Number(row.data.wage) * 0.0765).toFixed(2)}/hr)</div>}
                            <div>Insurance: ${defaultInsurance}/hr</div>
                            <div>Vacation: ${defaultVacation}/hr</div>
                            {row.data.phoneNumber && <div>Phone: {row.data.phoneNumber}</div>}
                            {row.data.tdlr && <div>TDLR: {row.data.tdlr}</div>}
                          </div>
                        )}
                      </td>
                      <td className="px-3 py-2 text-center">
                        {row.selectedEmployeeId === 'CREATE_NEW' ? (
                          <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-blue-100 text-blue-800">
                            New Employee
                          </span>
                        ) : row.selectedEmployeeId ? (
                          <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-green-100 text-green-800">
                            Matched
                          </span>
                        ) : (
                          <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-red-100 text-red-800">
                            Unmatched
                          </span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            
            {/* Detailed Update Preview */}
            {getMatchedCount() > 0 && (
              <div className="mt-6 bg-blue-50 border border-blue-200 rounded p-4">
                <h4 className="font-semibold text-blue-900 mb-3">Update Preview</h4>
                <div className="max-h-40 overflow-y-auto space-y-2">
                  {matchingRows
                    .filter(row => row.selectedEmployeeId)
                    .map((row, index) => {
                      const emp = allEmployees.find(e => e.id === row.selectedEmployeeId);
                      if (!emp) return null;
                      
                      const changes = [];
                      if (row.data.wage && Number(row.data.wage) !== emp.hourlyWage) {
                        const newWage = Number(row.data.wage);
                        const newFica = newWage * 0.0765;
                        changes.push(`Wage: $${emp.hourlyWage || 0} → $${newWage} (FICA: $${newFica.toFixed(2)})`);
                      }
                      if (row.data.jobTitle && row.data.jobTitle !== emp.jobTitle) {
                        changes.push(`Job: "${emp.jobTitle || 'none'}" → "${row.data.jobTitle}"`);
                      }
                      if (row.data.phoneNumber && row.data.phoneNumber !== emp.phone) {
                        changes.push(`Phone: "${emp.phone || 'none'}" → "${row.data.phoneNumber}"`);
                      }
                      if (row.data.tdlr && row.data.tdlr !== emp.tdlrNumber) {
                        changes.push(`TDLR: "${emp.tdlrNumber || 'none'}" → "${row.data.tdlr}"`);
                      }
                      if (row.data.employmentStatus) {
                        const newStatus = String(row.data.employmentStatus).toLowerCase().includes('terminated') ? 'terminated' : 'active';
                        if (newStatus !== emp.status) {
                          changes.push(`Status: ${emp.status} → ${newStatus}`);
                        }
                      }
                      
                      return (
                        <div key={index} className="text-xs bg-white p-2 rounded border">
                          <div className="font-medium text-gray-900">
                            {emp.name} ({row.excelName})
                          </div>
                          {changes.length > 0 ? (
                            <div className="text-gray-600 mt-1">
                              {changes.map((change, i) => (
                                <div key={i}>• {change}</div>
                              ))}
                            </div>
                          ) : (
                            <div className="text-green-600 mt-1">✓ All values match - no changes needed</div>
                          )}
                        </div>
                      );
                    })
                  }
                </div>
              </div>
            )}

            <div className="mt-4 flex justify-between items-center">
              <div className="text-sm text-gray-600">
                {getMatchedCount()} of {matchingRows.length} employees matched
              </div>
              <div className="space-x-2">
                <button
                  onClick={() => setMatchingMode(false)}
                  className="px-4 py-2 text-gray-600 border border-gray-300 rounded hover:bg-gray-50"
                >
                  Back to Preview
                </button>
                <button
                  onClick={processImport}
                  disabled={getMatchedCount() === 0}
                  className="px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Import {getMatchedCount()} Employees
                </button>
              </div>
            </div>
          </div>
        )}

        {importing && progress && (
          <div className="mb-4">
            <h3 className="text-lg font-semibold mb-2">Import Progress</h3>
            <div className="bg-gray-200 rounded-full h-2 mb-4">
              <div 
                className="bg-blue-600 h-2 rounded-full transition-all duration-300" 
                style={{ width: `${(progress.processed / progress.total) * 100}%` }}
              ></div>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-center">
              <div>
                <div className="text-2xl font-bold text-gray-800">{progress.processed}</div>
                <div className="text-sm text-gray-600">Processed</div>
              </div>
              <div>
                <div className="text-2xl font-bold text-green-600">{progress.matched}</div>
                <div className="text-sm text-gray-600">Matched</div>
              </div>
              <div>
                <div className="text-2xl font-bold text-blue-600">{progress.updated}</div>
                <div className="text-sm text-gray-600">Updated</div>
              </div>
              <div>
                <div className="text-2xl font-bold text-red-600">{progress.errors.length}</div>
                <div className="text-sm text-gray-600">Errors</div>
              </div>
            </div>
          </div>
        )}

        {showDefaultsPrompt && (
          <div className="mb-4">
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
              <h3 className="text-lg font-semibold text-blue-900 mb-4">Set Defaults for New Employees</h3>
              <div className="text-sm text-blue-800 mb-4">
                New employees will be created with these default values. FICA is calculated automatically at 7.65% of hourly wage.
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                <div>
                  <label className="block text-sm font-medium text-blue-900 mb-2">
                    Insurance Default
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    value={defaultInsurance}
                    onChange={(e) => setDefaultInsurance(Number(e.target.value))}
                    className="w-full px-3 py-2 border border-blue-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                  <div className="text-xs text-blue-600 mt-1">Per hour cost</div>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-blue-900 mb-2">
                    Vacation/Holiday Default
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    value={defaultVacation}
                    onChange={(e) => setDefaultVacation(Number(e.target.value))}
                    className="w-full px-3 py-2 border border-blue-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                  <div className="text-xs text-blue-600 mt-1">Per hour cost</div>
                </div>
              </div>
              
              <div className="flex justify-end space-x-3">
                <button
                  onClick={() => setShowDefaultsPrompt(false)}
                  className="px-4 py-2 text-blue-600 border border-blue-300 rounded-md hover:bg-blue-50"
                >
                  Cancel
                </button>
                <button
                  onClick={processImport}
                  className="px-6 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
                >
                  Continue Import
                </button>
              </div>
            </div>
          </div>
        )}

        {progress && !importing && (
          <div className="mb-4">
            <h3 className="text-lg font-semibold mb-2">Import Complete</h3>
            <div className="bg-green-50 border border-green-200 rounded p-4 mb-4">
              <p className="text-green-800">
                Successfully updated {progress.updated} employees out of {progress.total} rows processed.
                {progress.matched} employees were matched in the database.
              </p>
            </div>

            {progress.errors.length > 0 && (
              <div className="mb-4">
                <h4 className="font-semibold mb-2 text-red-600">Errors ({progress.errors.length})</h4>
                <div className="max-h-40 overflow-y-auto">
                  {progress.errors.map((error, index) => (
                    <div key={index} className="bg-red-50 border border-red-200 rounded p-2 mb-2">
                      <strong>Row {error.row} - {error.name}:</strong> {error.error}
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div className="flex justify-end space-x-2">
              <button
                onClick={() => {
                  setFile(null);
                  setPreviewData(null);
                  setProgress(null);
                  setMatchingRows([]);
                  setMatchingMode(false);
                }}
                className="px-4 py-2 text-gray-600 border border-gray-300 rounded hover:bg-gray-50"
              >
                Import Another File
              </button>
              <button
                onClick={onClose}
                className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
              >
                Close
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default EmployeeDataImporter;